import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import DeleteConfirmDialog from '@/components/ui/dialog/delete-confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import useUsers from '@/hooks/use-users';
import { centsToInitialValue } from '@/lib/currency';
import { cn } from '@/lib/utils';
import type { Deal, Payment } from '@/models/registry';
import dayjs from 'dayjs';
import {
  ArchiveRestore,
  Calendar,
  CalendarX,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleX,
  Ellipsis,
  ReceiptText,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  FORMAT_DATE_STRING,
  FORMAT_DATETIME_FRIENDLY_STRING,
} from './add-payment';
import EditPaymentDialog from './edit-payment-dialog';
import PaymentDeleteMenuItem, {
  useDeletePaymentMutation,
} from './payment-menu/payment-delete-menu-item';
import PaymentEditMenuItem from './payment-menu/payment-edit-menu-item';
import PaymentRestoreMenuItem, {
  useRestorePaymentMutation,
} from './payment-menu/payment-restore-menu-item';
import PaymentUploadReceiptButton from './payment-menu/payment-upload-receipt-button';
import { PaymentMethodBadge } from './payment-method-badge';
import { PAYMENT_METHOD_OPTIONS_MAP } from './payment-method-selector-combobox';
import { PaymentTypeBadge } from './payment-type-badge';
import { PAYMENT_TYPE_OPTIONS_MAP } from './payment-type-selector-combobox';

type PaymentStatus = 'pending' | 'paid' | 'deleted';

const getPaymentStatus = (payment: Payment): PaymentStatus => {
  if (payment.deleted_at) {
    return 'deleted';
  }

  if (payment.receipt_file_url) {
    return 'paid';
  }

  return 'pending';
};

const PaymentLine = ({
  payment,
  dealId,
}: {
  payment: Payment;
  dealId: Deal['id'];
}) => {
  const status = getPaymentStatus(payment);
  const paymentDateIsInThePast = dayjs(payment.intent_date).isBefore(dayjs());
  const requiresAlert = status == 'pending' && paymentDateIsInThePast;
  const isMobile = useIsMobile();

  const [deletedDialogIsOpen, setDeletedDialogIsOpen] = useState(false);
  const onOpen = useCallback(() => {
    setDeletedDialogIsOpen(true);
  }, [setDeletedDialogIsOpen]);
  const onClose = useCallback(() => {
    setDeletedDialogIsOpen(false);
  }, [setDeletedDialogIsOpen]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<
    Deal['payments'][0]['id'] | null
  >(null);
  const { deletePaymentMutation } = useDeletePaymentMutation({
    dealId,
    paymentId: selectedPaymentId,
  });
  const { restorePaymentMutation } = useRestorePaymentMutation({
    dealId,
    paymentId: payment.id,
  });

  const { users } = useUsers();
  const receiptUploader = users.find(
    (user) => user.id === payment.receipt_uploaded_by_id,
  );

  const [editPaymentDialogIsOpen, setEditPaymentDialogIsOpen] = useState(false);

  type PaymentDialog = 'edit' | 'delete';
  const [queuedPaymentDialog, setQueuedPaymentDialog] =
    useState<PaymentDialog | null>(null);

  return (
    <div
      className={cn('flex flex-row gap-2 items-center flex-nowrap', {
        'opacity-50': status == 'deleted',
      })}
    >
      <div className="flex flex-row">
        {status == 'deleted' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleX className="h-3.5 w-3.5 text-gray-500" />
            </TooltipTrigger>
            <TooltipContent align="start">
              <div>
                Deleted on{' '}
                {dayjs(payment.deleted_at).format(
                  FORMAT_DATETIME_FRIENDLY_STRING,
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        ) : status == 'pending' ? (
          paymentDateIsInThePast ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleAlert className="h-3.5 w-3.5 text-red-500" />
              </TooltipTrigger>
              <TooltipContent align="start">
                <div>Due {dayjs(payment.intent_date).fromNow()}</div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleDashed className="h-3.5 w-3.5 text-gray-500" />
              </TooltipTrigger>
              <TooltipContent align="start">
                <div>Pending</div>
              </TooltipContent>
            </Tooltip>
          )
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleCheck className="h-3.5 w-3.5 text-green-500" />
            </TooltipTrigger>
            <TooltipContent align="start">
              <div>Paid</div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-row gap-1 items-center text-uneditable w-17 whitespace-nowrap shrink-0">
            {requiresAlert ? (
              <CalendarX className="size-3.5 text-red-500 text-sm" />
            ) : (
              <Calendar className={cn('size-3.5 text-gray-500 text-sm', {})} />
            )}
            {payment.deleted_at
              ? dayjs(payment.deleted_at).format('MMM D')
              : payment.confirmed_date
                ? dayjs(payment.confirmed_date).format('MMM D')
                : payment.intent_date
                  ? dayjs(payment.intent_date).format('MMM D')
                  : 'N/A'}
          </div>
        </TooltipTrigger>
        <TooltipContent className="flex flex-row gap-1" align="start">
          {status == 'deleted' ? (
            <div>Deleted on {dayjs(payment.deleted_at).format('MMM D')}</div>
          ) : status == 'pending' ? (
            <div>Date of intent to pay</div>
          ) : status == 'paid' ? (
            <div>Date of receipt upload</div>
          ) : (
            <div>N/A</div>
          )}
          {/* <div>{dayjs(payment.date).format("MMM D, YYYY")}</div> */}
        </TooltipContent>
      </Tooltip>

      <CurrencyInput
        value={centsToInitialValue(payment.amount)}
        readOnly
        placeholder={''}
        className="text-uneditable text-right w-16"
      />

      <div className="flex flex-row grow-1 gap-2 items-center justify-end">
        <div className="flex flex-row gap-0 w-30">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer w-15">
                <PaymentMethodBadge value={payment.method} useAbbreviation />
              </div>
            </TooltipTrigger>
            <TooltipContent align="start">
              <div>
                {
                  PAYMENT_METHOD_OPTIONS_MAP.find(
                    (option) => option.value === payment.method,
                  )?.label
                }
              </div>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <PaymentTypeBadge value={payment.type} useAbbreviation />
              </div>
            </TooltipTrigger>
            <TooltipContent align="start">
              <div className="flex flex-row gap-1 items-center">
                {payment.is_full_payment ? 'Entire' : 'Partial'}
                {' payment for '}
                <div className="lowercase">
                  {
                    PAYMENT_TYPE_OPTIONS_MAP.find(
                      (option) => option.value === payment.type,
                    )?.label
                  }
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex flex-row gap-1 items-center shrink-0">
          <div className="flex flex-row gap-1 items-center shrink-0">
            {status == 'paid' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ReceiptText
                    className="h-3.5 w-3.5 text-green-500 cursor-pointer"
                    onClick={() => {
                      if (payment.receipt_file_url) {
                        window.open(payment.receipt_file_url, '_blank');
                      }
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent align="start">
                  {receiptUploader &&
                    `${receiptUploader?.first_name} ${receiptUploader?.last_name} uploaded the receipt on `}
                  {payment.confirmed_date &&
                    dayjs(payment.confirmed_date).format(FORMAT_DATE_STRING)}
                </TooltipContent>
              </Tooltip>
            )}
            {status == 'pending' && (
              <PaymentUploadReceiptButton
                paymentId={payment.id}
                dealId={dealId}
                requiresAlert={requiresAlert}
              />
            )}
            {status == 'deleted' && (
              // DEV NOTE: this is a hack to keep things aligned
              // this button is not visible but it's still in the DOM
              // if we want to show it - hook up the onClick to restore the payment
              <ArchiveRestore className="size-3.5 opacity-0" />
            )}
          </div>

          <DropdownMenu
            onOpenChange={(open) => {
              if (!open) {
                if (queuedPaymentDialog === 'edit') {
                  setEditPaymentDialogIsOpen(true);
                } else if (queuedPaymentDialog === 'delete') {
                  setDeletedDialogIsOpen(true);
                }
                setQueuedPaymentDialog(null);
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-1 h-fit">
                <Ellipsis className="h-3.5 w-3.5 text-gray-500 cursor-pointer" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Payment Actions
              </DropdownMenuLabel>
              <DropdownMenuItem>
                <PaymentEditMenuItem
                  payment={payment}
                  onSelect={() => {
                    setQueuedPaymentDialog('edit');
                  }}
                />
              </DropdownMenuItem>

              {status !== 'deleted' && (
                <DropdownMenuItem>
                  <PaymentDeleteMenuItem
                    payment={payment}
                    onSelect={() => {
                      setSelectedPaymentId(payment.id);
                      setQueuedPaymentDialog('delete');
                    }}
                  />
                </DropdownMenuItem>
              )}

              {status === 'deleted' && (
                <DropdownMenuItem>
                  <PaymentRestoreMenuItem
                    // dealId={dealId}
                    payment={payment}
                    onSelect={() => {
                      restorePaymentMutation.mutate();
                    }}
                  />
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DeleteConfirmDialog
        onConfirm={() => {
          if (!selectedPaymentId) {
            console.error('No payment id provided');
            return;
          }

          deletePaymentMutation.mutate({ paymentId: selectedPaymentId });
          setDeletedDialogIsOpen(false);
        }}
        dialogIsOpen={deletedDialogIsOpen}
        onOpen={onOpen}
        onClose={onClose}
        title={`Delete Payment for ${centsToInitialValue(payment.amount)}`}
        description="Please confirm you would like to delete the payment."
      />

      <EditPaymentDialog
        dealId={dealId}
        payment={payment}
        createdById={payment.created_by_id}
        ctaText={'Update Payment'}
        onOpen={() => {
          setEditPaymentDialogIsOpen(true);
        }}
        onClose={() => {
          setEditPaymentDialogIsOpen(false);
        }}
        dialogIsOpen={editPaymentDialogIsOpen}
        onSubmit={() => {
          setEditPaymentDialogIsOpen(false);
        }}
      />
    </div>
  );
};

export default PaymentLine;
